/**
 * Static column definitions for the detail tables on the results page. Each
 * column has at minimum `key` and `label`; optional fields:
 *   - `align`: "right" to right-align numeric cells
 *   - `format`: cell formatter ("euro" | "euroPrecise" | "euroMWh" | "volume" |
 *     "volumePrecise" | "boolean")
 *   - `srcField`: API field name (or array of fallback field names) used by
 *     `mapRows` to populate the cell
 *   - `scale`: multiplier applied to the raw API value before display
 */

const DATUM_COL = { key: "datum", label: "Datum" }
const TOTAAL_COL = {
  key: "totaal",
  label: "Totaal (€)",
  align: "right",
  format: "euro",
}
const COST_PER_CONNECTION_LABEL = "Kosten/aansl. (€)"

function marketColumns(fieldMap) {
  const base = [
    DATUM_COL,
    {
      key: "prijsVerkoop",
      label: "Prijs verkoop (€/MWh)",
      align: "right",
      format: "euroMWh",
    },
    {
      key: "volumeVerkoop",
      label: "Volume verkoop (kWh)",
      align: "right",
      format: "volume",
    },
    {
      key: "resultaatVerkoop",
      label: "Resultaat verkoop (€)",
      align: "right",
      format: "euro",
    },
    {
      key: "prijsInkoop",
      label: "Prijs inkoop (€/MWh)",
      align: "right",
      format: "euroMWh",
    },
    {
      key: "volumeInkoop",
      label: "Volume inkoop (kWh)",
      align: "right",
      format: "volume",
    },
    {
      key: "resultaatInkoop",
      label: "Resultaat inkoop (€)",
      align: "right",
      format: "euro",
    },
    TOTAAL_COL,
  ]
  return base.map((col) =>
    fieldMap[col.key] ? { ...col, srcField: fieldMap[col.key] } : col,
  )
}

export const DAGMARKT_COLS = marketColumns({
  prijsVerkoop: "DAY_AHEAD_FEED_IN_PRICE",
  volumeVerkoop: "DAY_AHEAD_FEED_IN_VOLUME",
  resultaatVerkoop: "DAY_AHEAD_FEED_IN_RESULT",
  prijsInkoop: "DAY_AHEAD_TAKE_OFF_PRICE",
  volumeInkoop: "DAY_AHEAD_TAKE_OFF_VOLUME",
  resultaatInkoop: "DAY_AHEAD_TAKE_OFF_RESULT",
  totaal: "DAY_AHEAD_TOTAL_RESULT",
})

// Portfolio-level records expose PORTFOLIO_IMBALANCE_* instead of ALLOCATION_POINT_IMBALANCE_*;
// srcField accepts an array of fallback field names, tried in order (see mapRows).
export const ONBALANS_COLS = marketColumns({
  prijsVerkoop: [
    "ALLOCATION_POINT_IMBALANCE_FEED_IN_PRICE",
    "PORTFOLIO_IMBALANCE_FEED_IN_PRICE",
  ],
  volumeVerkoop: [
    "ALLOCATION_POINT_IMBALANCE_FEED_IN_VOLUME",
    "PORTFOLIO_IMBALANCE_FEED_IN_VOLUME",
  ],
  resultaatVerkoop: [
    "ALLOCATION_POINT_IMBALANCE_FEED_IN_RESULT",
    "PORTFOLIO_IMBALANCE_FEED_IN_RESULT",
  ],
  prijsInkoop: [
    "ALLOCATION_POINT_IMBALANCE_TAKE_OFF_PRICE",
    "PORTFOLIO_IMBALANCE_TAKE_OFF_PRICE",
  ],
  volumeInkoop: [
    "ALLOCATION_POINT_IMBALANCE_TAKE_OFF_VOLUME",
    "PORTFOLIO_IMBALANCE_TAKE_OFF_VOLUME",
  ],
  resultaatInkoop: [
    "ALLOCATION_POINT_IMBALANCE_TAKE_OFF_RESULT",
    "PORTFOLIO_IMBALANCE_TAKE_OFF_RESULT",
  ],
  totaal: [
    "ALLOCATION_POINT_IMBALANCE_TOTAL_RESULT",
    "PORTFOLIO_IMBALANCE_TOTAL_RESULT",
  ],
})

export const INTRADAGMARKT_COLS = marketColumns({
  prijsVerkoop: "INTRADAY_SELL_PRICE",
  volumeVerkoop: "INTRADAY_SELL_VOLUME",
  resultaatVerkoop: "INTRADAY_SELL_RESULT",
  prijsInkoop: "INTRADAY_BUY_PRICE",
  volumeInkoop: "INTRADAY_BUY_VOLUME",
  resultaatInkoop: "INTRADAY_BUY_RESULT",
  totaal: "INTRADAY_TOTAL_RESULT",
})

export const FCR_COLS = [
  DATUM_COL,
  {
    key: "capaciteit",
    label: "Capaciteit (MW)",
    align: "right",
    format: "volumePrecise",
    srcField: "FCR_CAPACITY",
    scale: 1 / 1000,
  },
  {
    key: "prijs",
    label: "Prijs (€/MW)",
    align: "right",
    format: "euro",
    srcField: "FCR_PRICE",
    scale: 1000,
  },
  { ...TOTAAL_COL, srcField: "FCR_RESULT" },
]

export const AFRR_COLS = [
  DATUM_COL,
  {
    key: "volume",
    label: "Geactiveerd volume (kWh)",
    align: "right",
    format: "volume",
    srcField: "AFRR_ASSET_ACTIVATED_VOLUME",
  },
  {
    key: "resultaatActivatie",
    label: "Resultaat activatie (€)",
    align: "right",
    format: "euro",
    srcField: "AFRR_ASSET_ACTIVATED_RESULT",
  },
  {
    key: "poolInkomsten",
    label: "Pool-inkomsten (€)",
    align: "right",
    format: "euro",
    srcField: "AFRR_SHARED_CONTRIBUTION",
  },
  { ...TOTAAL_COL, srcField: "AFRR_RESULT" },
]

export const CONGESTIE_COLS = [
  DATUM_COL,
  {
    key: "gemisteProductie",
    label: "Gemiste productie (kWh)",
    align: "right",
    format: "volume",
  },
  {
    key: "compensatie",
    label: "Compensatie gemiste productie (€)",
    align: "right",
    format: "euro",
  },
  {
    key: "gemisteProductieVergoeding",
    label: "Gemiste productievergoeding (€)",
    align: "right",
    format: "euro",
  },
  {
    key: "vasteVergoedingCBC",
    label: "Vaste vergoeding netbeheerder CBC (€)",
    align: "right",
    format: "euro",
  },
  {
    key: "afroep",
    label: "Afroepvergoeding (€)",
    align: "right",
    format: "euro",
  },
  {
    key: "resultaatCBC",
    label: "Resultaat CBC (€)",
    align: "right",
    format: "euro",
  },
  {
    key: "vasteVergoedingCSC",
    label: "Vaste vergoeding netbeheerder CSC (€)",
    align: "right",
    format: "euro",
  },
  {
    key: "resultaatCSC",
    label: "Resultaat CSC (€)",
    align: "right",
    format: "euro",
  },
  TOTAAL_COL,
]

export const PORTFOLIO_VOORDEEL_COLS = [
  DATUM_COL,
  { ...TOTAAL_COL, srcField: "PORTFOLIO_BENEFIT_REPOWERED" },
]

export const VASTE_VERGOEDING_COLS = [
  { key: "datum", label: "Maand" },
  {
    key: "aansluitingenEnergielevering",
    label: "Aansl. energielevering",
    align: "right",
  },
  {
    key: "kostenEnergielevering",
    label: COST_PER_CONNECTION_LABEL,
    align: "right",
    format: "euro",
  },
  {
    key: "vergoedingEnergielevering",
    label: "Vaste verg. energielevering (€)",
    align: "right",
    format: "euro",
  },
  {
    key: "aansluitingenCongestie",
    label: "Aansl. congestiediensten",
    align: "right",
  },
  {
    key: "kostenCongestie",
    label: COST_PER_CONNECTION_LABEL,
    align: "right",
    format: "euro",
  },
  {
    key: "vergoedingCongestie",
    label: "Vaste verg. congestiediensten (€)",
    align: "right",
    format: "euro",
  },
  {
    key: "aansluitingenBalancering",
    label: "Aansl. balanceringsdiensten",
    align: "right",
  },
  {
    key: "kostenBalancering",
    label: COST_PER_CONNECTION_LABEL,
    align: "right",
    format: "euro",
  },
  {
    key: "vergoedingBalancering",
    label: "Vaste verg. balanceringsdiensten (€)",
    align: "right",
    format: "euro",
  },
  TOTAAL_COL,
]

export const VARIABELE_VERGOEDING_COLS = [
  DATUM_COL,
  {
    key: "volume",
    label: "Absoluut gemeten volume (kWh)",
    align: "right",
    format: "volume",
  },
  {
    key: "vergoeding",
    label: "Variabele vergoeding (€/kWh)",
    align: "right",
    format: "euroPrecise",
  },
  {
    key: "totaal",
    label: "Totaal (€)",
    align: "right",
    format: "euro",
  },
]

export const GEMISTE_PRODUCTIEVERGOEDING_COLS = [
  DATUM_COL,
  {
    key: "volume",
    label: "Volume (MWh)",
    align: "right",
    format: "volumePrecise",
    srcField: "CBC_MISSED_PRODUCTION",
    scale: 1 / 1000,
  },
  { ...TOTAAL_COL, srcField: "CBC_MISSED_PRODUCTION_RESULT" },
]

export const WINSTDELING_COLS = [
  { key: "datum", label: "Maand" },
  {
    key: "meerwaarde",
    label: "Meerwaarde Repowered (€)",
    align: "right",
    format: "euro",
  },
  { key: "percentage", label: "Winstdeling (%)", align: "right" },
  TOTAAL_COL,
]

export const WINSTDELING_FCR_COLS = [
  DATUM_COL,
  {
    key: "fcrResultaat",
    label: "FCR resultaat",
    align: "right",
    format: "euro",
  },
  {
    key: "percentage",
    label: "Percentage (%)",
    align: "right",
    format: "percentage",
  },
  TOTAAL_COL,
]

export function optimaFeeColumns(volumeUnit) {
  return [
    { key: "datum", label: "Maand" },
    {
      key: "volume",
      label: `Volume (${volumeUnit})`,
      align: "right",
      format: "volume",
    },
    {
      key: "tarief",
      label: `Tarief (€/${volumeUnit})`,
      align: "right",
      format: "euroPrecise",
    },
    TOTAAL_COL,
  ]
}

export function optimaColumns(assetLabel) {
  return [
    { key: "datum", label: "Maand" },
    { key: "asset", label: assetLabel, align: "right" },
    {
      key: "vergoeding",
      label: "Vergoeding (€)",
      align: "right",
      format: "euro",
    },
    TOTAAL_COL,
  ]
}

// Energy tax is billed across this many consumption zones (staffel).
const ENERGY_TAX_ZONE_COUNT = 4

export const ENERGIEBELASTING_COLS = [
  DATUM_COL,
  ...Array.from(
    { length: ENERGY_TAX_ZONE_COUNT },
    (_, index) => index + 1,
  ).flatMap((zone) => [
    {
      key: `tariefZone${zone}`,
      label: `Tarief zone ${zone} (€/kWh)`,
      align: "right",
      format: "euroPrecise",
    },
    {
      key: `volumeZone${zone}`,
      label: `Volume zone ${zone} (kWh)`,
      align: "right",
      format: "volume",
    },
    {
      key: `voorschotZone${zone}`,
      label: `Voorschot zone ${zone} (€)`,
      align: "right",
      format: "euro",
    },
  ]),
  { ...TOTAAL_COL, srcField: "ENERGY_TAX" },
]

export const BELASTINGVERMINDERING_COLS = [
  { key: "datum", label: "Maand" },
  { ...TOTAAL_COL, srcField: "ENERGY_TAX_REBATE" },
]
